/**
 * liveToday.js — ONE client for the Live Content Registry (GET /api/live/today).
 * One date/time service for every Live Today surface: server clock offset, countdowns, boundary refresh.
 * Short cache (5 min, per tier) that is dropped as soon as the server's nextRefreshAt passes.
 */
import { useEffect, useState } from "react";
import { track } from "@/lib/track";
import { useTier } from "@/lib/ageGroup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "mwq_live_today_v2";
const TTL = 5 * 60e3;
export const TIER_API = { seedlings: "seedling", explorers: "explorer", pioneers: "pioneer" };

let offset = 0; // serverNow - clientNow (ms)
export const serverNow = () => Date.now() + offset;

function anonId() { try { return localStorage.getItem("wq_anon_id") || ""; } catch { return ""; } }
function readCache(tier) {
  try {
    const c = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (!c || c.tier !== tier || Date.now() - c.at > TTL) return null;
    if (c.data.nextRefreshAt && new Date(c.data.nextRefreshAt).getTime() <= serverNow()) return null;
    return c.data;
  } catch { return null; }
}

export async function fetchLiveToday(tier, { force = false } = {}) {
  const cached = !force && readCache(tier);
  if (cached) return cached;
  const q = new URLSearchParams({ tier }); const a = anonId(); if (a) q.set("anon_id", a);
  const r = await fetch(`${API}/live/today?${q}`);
  if (!r.ok) throw new Error(`live/today ${r.status}`);
  const data = await r.json();
  if (data.serverNow) offset = new Date(data.serverNow).getTime() - Date.now();
  try { sessionStorage.setItem(KEY, JSON.stringify({ tier, at: Date.now(), data })); } catch { /* ignore */ }
  return data;
}

/** Items that are genuinely current right now (server clock); ENDED/UPCOMING/NOT_READY never render. */
export function currentItems(data) {
  if (!data?.items) return [];
  const now = serverNow();
  return data.items.filter((i) => i.childVisible !== false && (i.status === "ACTIVE" || i.status === "ON_DEMAND")
    && (!i.endAt || new Date(i.endAt).getTime() > now) && (!i.startAt || new Date(i.startAt).getTime() <= now));
}

/** Human countdown from the server clock — never a hard-coded "this week". */
export function timeLeft(endAt) {
  if (!endAt) return null;
  const ms = new Date(endAt).getTime() - serverNow();
  if (ms <= 0) return "Ended";
  const d = Math.floor(ms / 864e5), h = Math.floor(ms / 36e5), m = Math.max(1, Math.floor(ms / 6e4));
  if (d >= 1) return `Ends in ${d} day${d === 1 ? "" : "s"}`;
  if (h >= 1) return `Ends in ${h} hour${h === 1 ? "" : "s"}`;
  return `Ends in ${m} min`;
}

const seenIds = new Set();
export function impression(item) {
  const repeat = seenIds.has(item.liveActivityId); seenIds.add(item.liveActivityId);
  track("live_card_impression", { id: item.liveActivityId, type: item.type, status: item.status, completed: !!item.completed });
  if (repeat) track("repeat_exposure", { id: item.liveActivityId, type: item.type });
}

/** Loads on mount, when the tab becomes visible again, at the next event boundary, and re-ticks countdowns every minute. */
export function useLiveToday(tierId, { force = false } = {}) {
  const tier = TIER_API[tierId] || "explorer";
  const [data, setData] = useState(() => (force ? null : readCache(tier)));
  const [error, setError] = useState(false);
  const [, setTick] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let on = true, boundary = null;
    const load = (f) => fetchLiveToday(tier, { force: f }).then((d) => {
      if (!on) return;
      setData((prev) => { if (prev) prev.items?.forEach((p) => { if (!d.items?.some((n) => n.liveActivityId === p.liveActivityId)) track("event_replaced", { id: p.liveActivityId, type: p.type }); }); return d; });
      setError(false);
      window.clearTimeout(boundary);
      const next = d.nextRefreshAt ? new Date(d.nextRefreshAt).getTime() - serverNow() + 1500 : null;
      if (next && next > 0) boundary = window.setTimeout(() => { track("event_expired", { at: d.nextRefreshAt }); load(true); }, Math.min(next, 6 * 36e5));
    }).catch(() => { if (on) setError(true); });
    load(force || reloadKey > 0);
    const vis = () => { if (document.visibilityState === "visible") load(false); };
    document.addEventListener("visibilitychange", vis);
    const tick = window.setInterval(() => setTick((t) => t + 1), 60e3);
    return () => { on = false; document.removeEventListener("visibilitychange", vis); window.clearTimeout(boundary); window.clearInterval(tick); };
  }, [tier, force, reloadKey]);
  const reload = () => { try { sessionStorage.removeItem(KEY); } catch { /* ignore */ } setReloadKey((k) => k + 1); };
  return { data, items: error ? [] : currentItems(data), error, reload, tier };
}

/**
 * ONE live instance for a page (/live/daily, /live/tournament): always fresh from the registry so the page,
 * the homepage strip and /live agree on id, date, question count, status and completion.
 */
export function useLiveInstance(type) {
  const tierId = useTier() || "explorers";
  const { data, error, reload, tier } = useLiveToday(tierId, { force: true });
  const item = data?.items?.find((i) => i.type === type) || null;
  const stale = !!(item?.endAt && new Date(item.endAt).getTime() <= serverNow());
  return { item: stale ? null : item, data, loading: !data && !error, error, reload, tier, stale };
}
